import { useState } from "react";
import { Icon } from "./Icon";
import { useAuthStore } from "../store/auth";
import { DEMO_MODE } from "../lib/config";
import { useAuditLog } from "../hooks/useAuditLog";

interface Props {
  onUnlock: () => void;
}

export function LockOverlay({ onUnlock }: Props) {
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const audit = useAuditLog();

  const handleUnlock = async () => {
    if (DEMO_MODE) {
      audit({ action: "unlock" });
      onUnlock();
      return;
    }
    if (!user) return;
    setBusy(true);
    setErr(null);
    try {
      await login(user.email, password);
      audit({ action: "unlock" });
      setPassword("");
      onUnlock();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lock-overlay" role="dialog" aria-modal="true" aria-labelledby="lock-title">
      <div className="lock-overlay__card">
        <div className="lock-overlay__icon">
          <Icon.Lock width={28} height={28} />
        </div>
        <h2 id="lock-title" className="lock-overlay__title">Session locked</h2>
        <p className="lock-overlay__body">
          Your session was idle for too long. Re-enter your password to continue. All in-memory PHI has been cleared.
        </p>

        {!DEMO_MODE && (
          <label className="form__row" style={{ marginBottom: 16 }}>
            <span className="form__label" style={{ textAlign: "left" }}>
              Password for {user?.email ?? "—"}
            </span>
            <input
              className="form__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUnlock();
              }}
            />
          </label>
        )}

        {err && <p className="form__error" style={{ marginBottom: 12 }}>{err}</p>}

        <button
          className="btn btn--solid"
          onClick={handleUnlock}
          disabled={busy || (!DEMO_MODE && !password)}
          style={{ width: "100%", justifyContent: "center" }}
        >
          {busy ? "Verifying…" : DEMO_MODE ? "Unlock (demo)" : "Unlock"}
        </button>
      </div>
    </div>
  );
}
